import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Wand2, X, Download, Loader2, Sparkles, AlertCircle, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ImageGenerator = ({ onClose, onImageGenerated }) => {
  const [prompt, setPrompt] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [error, setError] = useState(null);

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error("Please enter a description for your image");
      return;
    }

    setIsGenerating(true);
    setError(null);
    setGeneratedImage(null);

    try {
      const response = await fetch(`${API}/generate-image`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ prompt: prompt.trim() }),
      });

      const data = await response.json();

      if (data.success) {
        setGeneratedImage({
          data: data.image_data,
          mimeType: data.mime_type,
          textResponse: data.text_response
        });
        toast.success("Image generated successfully!");
      } else {
        if (data.error === "budget_exceeded") {
          setError("AI credits are low. Please add balance in Profile → Universal Key → Add Balance");
        } else {
          setError(data.message || "Failed to generate image");
        }
      }
    } catch (err) {
      console.error("Image generation error:", err);
      setError("Failed to connect to the server. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = () => {
    if (!generatedImage) return;
    
    const link = document.createElement('a');
    link.href = `data:${generatedImage.mimeType};base64,${generatedImage.data}`;
    link.download = `nexi-image-${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Image downloaded!");
  };

  const handleUseInChat = () => {
    if (!generatedImage) return;
    
    // Pass the image back to chat
    if (onImageGenerated) {
      onImageGenerated({
        type: "generated",
        data: generatedImage.data,
        mimeType: generatedImage.mimeType,
        prompt: prompt
      });
    }
    toast.success("Image added to chat!");
    onClose();
  };

  const suggestions = [
    "A futuristic city at sunset with flying cars",
    "A cozy coffee shop on a rainy day",
    "A magical forest with glowing mushrooms",
    "A cute robot learning to paint",
    "An underwater kingdom with colorful coral"
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="w-full max-w-2xl bg-card border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
              <Wand2 size={20} className="text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">AI Image Generator</h2>
              <p className="text-xs text-white/50">Powered by Gemini Nano Banana</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/10 transition-colors"
            data-testid="close-image-generator"
          >
            <X size={20} className="text-white/70" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Prompt Input */}
          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">
              Describe your image
            </label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="A majestic mountain landscape at golden hour with a lake reflection..."
              className="w-full h-24 bg-black/30 border border-white/10 rounded-xl p-3 text-white placeholder:text-white/30 resize-none focus:outline-none focus:border-primary/50"
              disabled={isGenerating}
              data-testid="image-prompt-input"
            />
          </div>

          {/* Quick Suggestions */}
          {!generatedImage && !isGenerating && (
            <div>
              <p className="text-xs text-white/40 mb-2">Try these:</p>
              <div className="flex flex-wrap gap-2">
                {suggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    onClick={() => setPrompt(suggestion)}
                    className="text-xs px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full text-white/70 hover:text-white transition-all"
                  >
                    {suggestion.substring(0, 30)}...
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Error Display */}
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="flex items-start gap-3 p-3 bg-red-500/10 border border-red-500/30 rounded-xl"
              >
                <AlertCircle size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-300">{error}</p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Loading State */}
          {isGenerating && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-col items-center justify-center py-12"
            >
              <div className="relative">
                <Loader2 size={48} className="text-primary animate-spin" />
                <Sparkles size={20} className="text-secondary absolute -top-1 -right-1 animate-pulse" />
              </div>
              <p className="mt-4 text-white/70 font-mono text-sm">Creating your image...</p>
              <p className="text-white/40 text-xs mt-1">This may take 10-20 seconds</p>
            </motion.div>
          )}

          {/* Generated Image */}
          <AnimatePresence>
            {generatedImage && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="space-y-3"
              >
                <div className="relative rounded-xl overflow-hidden border border-white/10">
                  <img
                    src={`data:${generatedImage.mimeType};base64,${generatedImage.data}`}
                    alt={prompt}
                    className="w-full h-auto"
                    data-testid="generated-image"
                  />
                </div>
                
                {/* Action Buttons */}
                <div className="flex gap-2">
                  <motion.button
                    onClick={handleDownload}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-white/10 hover:bg-white/20 rounded-xl text-white font-medium transition-all"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    data-testid="download-image"
                  >
                    <Download size={18} />
                    Download
                  </motion.button>
                  <motion.button
                    onClick={handleUseInChat}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-primary to-secondary rounded-xl text-white font-medium transition-all"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    data-testid="use-in-chat"
                  >
                    <ImageIcon size={18} />
                    Use in Chat
                  </motion.button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-white/10">
          <motion.button
            onClick={handleGenerate}
            disabled={!prompt.trim() || isGenerating}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl text-white font-bold transition-all shadow-lg shadow-primary/30"
            whileHover={{ scale: isGenerating ? 1 : 1.02 }}
            whileTap={{ scale: isGenerating ? 1 : 0.98 }}
            data-testid="generate-image-button"
          >
            {isGenerating ? (
              <>
                <Loader2 size={20} className="animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Wand2 size={20} />
                {generatedImage ? "Generate New Image" : "Generate Image"}
              </>
            )}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ImageGenerator;
